import api from "./api";

/**
 * The coach's note writes, as the requests alone (#1221 slice 3, D57).
 *
 * Feedback notes (`/notes`, the player reads them) and private notes
 * (`/coach-reflections`, the player never does) are two tables behind two
 * endpoints and stay two sets of calls: nothing here turns one into the other.
 * No request or response shape changes from the handlers these were lifted from.
 *
 * `CoachPortal.jsx` owns the state these close over and the shipped page's
 * handlers (`addNote`, `deleteNote`, `addReflection`, ...), which wrap each of
 * these with the toast and the `confirm()` they have always had. The coach's
 * player page calls them as they are: each THROWS on failure and toasts
 * nothing, so the panel that asked can show the failure where it happened
 * instead of a toast that goes away. Lifted out of the page because that
 * function is at its line budget (`eslint.config.mjs`), the same reason
 * `coachGoalWrites.js` exists.
 *
 * `loadNotes` and `loadReflections` reload after a write and never throw (the
 * page's `guardedLoad` catches), so a write that succeeded is not reported
 * failed because the reload did.
 */
export function coachNoteWrites({
  selected, feedback, category, reflectionBody, editingReflection,
  setFeedback, setCategory, setReflectionBody, setEditingReflection,
  loadNotes, loadReflections,
}) {
  const postNote = async () => {
    await api.post("/notes", { player_id: selected.player_id, feedback, category, pinned: false });
    setFeedback("");
    setCategory("general");
    loadNotes();
  };

  const removeNote = async (n) => {
    await api.delete(`/notes/${n.note_id}`);
    loadNotes();
  };

  const postReflection = async () => {
    await api.post("/coach-reflections", { player_id: selected.player_id, body: reflectionBody });
    setReflectionBody("");
    loadReflections();
  };

  const patchReflection = async () => {
    // The API refuses an empty body for good (400); never send one.
    if (!editingReflection?.body?.trim()) return;
    await api.patch(`/coach-reflections/${editingReflection.reflection_id}`, { body: editingReflection.body });
    setEditingReflection(null);
    loadReflections();
  };

  const removeReflection = async (r) => {
    await api.delete(`/coach-reflections/${r.reflection_id}`);
    loadReflections();
  };

  return { postNote, removeNote, postReflection, patchReflection, removeReflection };
}
